/**
 * Scoring Engine Orchestrator
 * 
 * Single entry point called on assessment completion.
 * Pipeline: User Response → MCF Value (0-100) → MC → Section → Pillar → Overall
 */

import { supabase } from '@/integrations/supabase/client';
import { extractMcfValue } from './scoringEngine';
import { extractCalculatedMcf, calculateLiveDataMcfs } from './calculatedMcfs';
import { aggregateAllScores } from './scoringAggregation';
import { persistMcfValues, persistScores } from './scoringPersistence';
import type { McfExtractionResult } from './types';

export async function calculateScores(
  userId: string,
  teamId: string | null,
  assessmentId?: string | null
): Promise<{ overallScore: number; success: boolean }> {

  try {
    // 0. Resolve teamId if not provided
    let resolvedTeamId = teamId;
    if (!resolvedTeamId) {
      const { data: teamData } = await supabase
        .from('team_members')
        .select('team_id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .limit(1)
        .maybeSingle();
      
      resolvedTeamId = teamData?.team_id || null;
    }

    if (!resolvedTeamId) {
      // Fallback: use userId as synthetic team scope for solo users (no team_members row)
      resolvedTeamId = userId;
      console.info(`[Scoring] No team found for user — using userId as team scope fallback: ${resolvedTeamId}`);
    }

    // Guard: if no assessmentId, skip standard pipeline — run live-data MCFs only
    if (!assessmentId) {
      const liveDataMcfResults = await calculateLiveDataMcfs(userId, resolvedTeamId);
      if (liveDataMcfResults.length > 0) {
        await persistMcfValues(userId, resolvedTeamId, liveDataMcfResults);
      }

      // Load all existing MCF values for full aggregation
      const mcfValueMap = new Map<string, number>();
      const { data: existingMcfValues, error: mcfError } = await supabase
        .from('user_mcf_values')
        .select('mcf_id, calculated_value')
        .eq('user_id', userId)
        .eq('team_id', resolvedTeamId);

      if (mcfError) {
        console.error('[calculateScores] Failed to fetch existing MCF values:', mcfError.message);
        throw new Error(`Score calculation failed: ${mcfError.message}`);
      }

      for (const existing of existingMcfValues || []) {
        const calcValue = existing.calculated_value as Record<string, any> | null;
        if (calcValue?.score !== undefined) {
          mcfValueMap.set(existing.mcf_id, calcValue.score);
        }
      }

      // Overlay fresh live-data results
      for (const result of liveDataMcfResults) {
        if (result.score !== null) {
          mcfValueMap.set(result.mcfId, result.score);
        }
      }

      if (mcfValueMap.size > 0) {
        const { mcScores, sectionScores, pillarScores, overallScore } =
          await aggregateAllScores(mcfValueMap);
        await persistScores(userId, resolvedTeamId, mcScores, sectionScores, pillarScores, overallScore);
        return { overallScore: overallScore.score, success: true };
      }

      return { overallScore: 0, success: true };
    }

    // 1. Fetch user responses for this assessment
    const { data: responses, error: respError } = await supabase
      .from('user_assessment_responses')
      .select('question_id, response_value')
      .eq('user_id', userId)
      .eq('assessment_id', assessmentId);

    if (respError || !responses) {
      console.error('[Scoring] Failed to fetch responses:', respError);
      return { overallScore: 0, success: false };
    }

    // 2. Fetch question definitions (need options JSONB for scoring rules)
    const { data: questions } = await supabase
      .from('assessment_questions')
      .select('id, question_key, question_type, options')
      .eq('assessment_id', assessmentId);

    if (!questions) {
      console.error('[Scoring] Failed to fetch questions');
      return { overallScore: 0, success: false };
    }

    const questionMap = new Map(questions.map(q => [q.id, q]));
    const questionKeyMap = new Map(questions.map(q => [q.question_key, q]));

    // 3. Fetch MCF data source mappings (source_reference = question_key)
    const questionKeys = questions.map(q => q.question_key).filter(Boolean);
    const { data: mcfSources } = await supabase
      .from('mcf_data_sources')
      .select('id, mcf_id, source_type, source_reference, calculation_formula, transform_logic')
      .in('source_reference', questionKeys);

    if (!mcfSources || mcfSources.length === 0) {
      return { overallScore: 0, success: false };
    }

    // 4. Fetch MCF codes for logging
    const mcfIds = mcfSources.map(s => s.mcf_id);
    const { data: mcfDefs } = await supabase
      .from('measurement_component_factors')
      .select('id, code')
      .in('id', mcfIds);

    const mcfCodeMap = new Map((mcfDefs || []).map(m => [m.id, m.code]));

    // 5. Build response lookup: question_key → response_value
    const responseLookup = new Map<string, any>();
    for (const resp of responses) {
      const question = questionMap.get(resp.question_id);
      if (question?.question_key) {
        responseLookup.set(question.question_key, resp.response_value);
      }
    }

    // 6. Extract MCF values from responses
    const mcfResults: McfExtractionResult[] = [];

    // Build a flat question_key → response_value map for calculated MCFs
    const allResponsesByKey: Record<string, any> = {};
    for (const resp of responses) {
      const question = questionMap.get(resp.question_id);
      if (question?.question_key) {
        allResponsesByKey[question.question_key] = resp.response_value;
      }
    }

    for (const source of mcfSources) {
      // Handle calculated MCFs (combine multiple responses)
      if (source.source_type === 'calculated') {
        const mcfCode = mcfCodeMap.get(source.mcf_id) || '';
        const score = await extractCalculatedMcf(mcfCode, allResponsesByKey, resolvedTeamId || '');
        mcfResults.push({
          mcfId: source.mcf_id,
          mcfCode,
          score,
          rawAnswer: { type: 'calculated', formula: source.calculation_formula },
          calculationMethod: 'calculated',
          sourceQuestionKey: source.source_reference || ''
        });
        continue;
      }

      const question = questionKeyMap.get(source.source_reference || '');
      if (!question) {
        console.warn(`[Scoring] No question found for source_reference: ${source.source_reference}`);
        continue;
      }

      const responseValue = responseLookup.get(source.source_reference || '');
      if (responseValue === undefined || responseValue === null) {
        continue;
      }

      // Handle sub-field extraction via transform_logic
      let effectiveResponse = responseValue;
      const transformLogic = source.transform_logic as Record<string, any> | null;
      if (transformLogic && typeof transformLogic === 'object' && Object.keys(transformLogic).length > 0) {
        const extractField = transformLogic.extract_field;
        if (extractField && typeof responseValue === 'object' && responseValue !== null) {
          effectiveResponse = responseValue[extractField];
          if (effectiveResponse === undefined) {
            console.warn(`[Scoring] transform_logic extract_field '${extractField}' not found in response for ${source.source_reference}`);
            continue;
          }
        }
      }

      const { score, calculationMethod } = extractMcfValue(
        effectiveResponse,
        source.source_type,
        source.calculation_formula,
        question.options as unknown as Parameters<typeof extractMcfValue>[3],
        source.transform_logic as { type?: string; tiers?: Array<{ min: number; score: number }> } | null
      );

      mcfResults.push({
        mcfId: source.mcf_id,
        mcfCode: mcfCodeMap.get(source.mcf_id) || '',
        score,
        rawAnswer: effectiveResponse,
        calculationMethod,
        sourceQuestionKey: source.source_reference || ''
      });
    }

    // 7. Persist MCF values
    await persistMcfValues(userId, resolvedTeamId, mcfResults);

    // 8. Build MCF value map for aggregation
    const mcfValueMap = new Map<string, number>();
    for (const result of mcfResults) {
      if (result.score !== null) {
        mcfValueMap.set(result.mcfId, result.score);
      }
    }

    // 8b. Always-on live-data MCF handlers (team_members-based, assessment-independent)
    const liveDataMcfResults = await calculateLiveDataMcfs(userId, resolvedTeamId);
    if (liveDataMcfResults.length > 0) {
      await persistMcfValues(userId, resolvedTeamId, liveDataMcfResults);
      for (const result of liveDataMcfResults) {
        if (result.score !== null) {
          mcfValueMap.set(result.mcfId, result.score);
        }
      }
    }

    // 9. Load existing MCF values from OTHER assessments
    const { data: existingMcfValues } = await supabase
      .from('user_mcf_values')
      .select('mcf_id, calculated_value')
      .eq('user_id', userId)
      .eq('team_id', resolvedTeamId);

    for (const existing of existingMcfValues || []) {
      const calcValue = existing.calculated_value as Record<string, any> | null;
      if (!mcfValueMap.has(existing.mcf_id) && calcValue?.score !== undefined) {
        mcfValueMap.set(existing.mcf_id, calcValue.score);
      }
    }

    // 10. Run full aggregation pipeline
    const { mcScores, sectionScores, pillarScores, overallScore } =
      await aggregateAllScores(mcfValueMap);

    // 11. Persist all aggregated scores
    await persistScores(userId, resolvedTeamId, mcScores, sectionScores, pillarScores, overallScore);

    return { overallScore: overallScore.score, success: true };

  } catch (error) {
    console.error('[Scoring] Calculation failed:', error);
    return { overallScore: 0, success: false };
  }
}
